import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Scrollbars } from 'react-custom-scrollbars-2'
import Alert from 'react-s-alert'
import { SwipeableDrawer } from '@material-ui/core'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { transparentize } from 'polished'
import { radius, space } from 'theme'
import { ERROR_MESSAGE, IMAGE } from 'consts'
import request from 'utils/request'
import errorHelper from 'utils/errorHelper'
import { Box, Flex } from 'components/atoms/Layout'
import Button from 'components/atoms/Button'
import { Text } from 'components/atoms/Typography'
import Icon from 'components/atoms/Icon'
import TextArea from 'components/atoms/TextArea'
import Switch from 'components/atoms/Switch'
import ImageWithFallback from 'components/atoms/ImageWithFallback'
import Image from 'components/atoms/Image'
import Input from 'components/atoms/Input'
import ButtonWithLoading from 'components/molecules/ButtonWithLoading'
import { POST_IMAGE, POST_TEXT, ROUTE_AI, ROUTE_MEDIA, TABS_TYPE_IMAGE_UNSPLASH } from 'routes/Calendar/consts'

const StyledAccordion = styled(Accordion)`
  background: ${({ theme }) => theme.colors.background_slider} !important;
  &:before {
  }
  padding: 0;

  .MuiAccordionDetails-root {
    padding: 0;
    color: ${({ theme }) => theme.colors.primaryText};
  }
`

const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 0 !important;
  min-height: 48px !important;
  .MuiAccordionSummary-content {
    margin: ${space.m} 0 !important;
  }
`

const StyledArrowWrapper = styled(Flex)`
  width: 24px;
  height: 24px;
  border-radius: ${radius.pill};
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.white};
  align-items: center;
  justify-content: center;
  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`

const StyledIdeaWrapper = styled(Flex)`
  padding: ${space.s};
  border-radius: ${radius.l};
  background: ${({ theme }) => theme.colors.background_card_secondary};
  &:hover {
    box-shadow: 0 0 5px 2px ${({ theme }) => transparentize(0.7, theme.colors.box_shadow)};
  }
`

const StyledIdeaImageWrapper = styled(ImageWithFallback)`
  border-radius: ${radius.l};
  width: 64px;
  height: 64px;
`

const StyledUseIdeaWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: ${radius.l};
  padding: ${space.xs} ${space.m};

  svg {
    fill: ${({ theme }) => theme.colors.primary};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background_card};
    svg {
      fill: ${({ theme }) => theme.colors.error};
    }
  }
`

const StyledText = styled(Text)`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const RemoveIconWrapper = styled(Box)`
  position: absolute;
  top: -9px;
  right: -6px;
  background: ${({ theme }) => theme.colors.background_card_secondary};
  height: 20px;
  width: 20px;
  border-radius: ${radius.pill};
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const StyledGeneratedText = styled(Text)`
  white-space: break-spaces;
`

const StyledButtonsWrapper = styled(Flex)`
  gap: ${space.s};
`

const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent)

const MAX_GENERATION_AMOUNT = 30
const DEFAULT_GENERATION_AMOUNT = 5

const { BUSINESS_ABOUT, TARGET_AUDIENCE, IMAGE_INCLUDE, IMAGE_KEYWORDS, GENERATION_AMOUNT } = {
  BUSINESS_ABOUT: 'business_about',
  TARGET_AUDIENCE: 'target_audience',
  IMAGE_INCLUDE: 'image_include',
  IMAGE_KEYWORDS: 'image_keywords',
  GENERATION_AMOUNT: 'generation_amount',
}

const GenerateBulkAIComponent = forwardRef(({ router, handleClickAddPosts }, generateBulkAIComponentRef) => {
  const formRef = useRef(null)
  const scrollbarsRef = useRef(null)

  const [isSliderOpen, setIsSliderOpen] = useState(false)
  const [usage, setUsage] = useState(null)
  const [isGettingData, setIsGettingData] = useState(false)
  const [usedImages, setUsedImages] = useState({})
  const [isFormExpanded, setIsFormExpanded] = useState(true)
  const [ideas, setIdeas] = useState([])
  const [formValues, setFormValues] = useState({})
  const [selectedPosts, setSelectedPosts] = useState([])

  useImperativeHandle(generateBulkAIComponentRef, () => ({
    handleClickOpenGenerateIdeasSlider() {
      // eslint-disable-next-line no-use-before-define
      handleClickOpenGenerateIdeasSlider()
    },
  }))

  const { remaining } = usage || {}

  const getUsage = async () => {
    try {
      const response = await request({
        method: 'GET',
        path: `${ROUTE_AI}/usage`,
      })
      const { error, data } = response || {}
      if (!response || error) {
        Alert.error(error || ERROR_MESSAGE, { timeout: 5000 })
      } else {
        const { type } = data || {}
        if (type === 'total') {
          setUsage({ ...data })
        }
      }
    } catch (error) {
      errorHelper({ error, componentName: GenerateBulkAIComponent.displayName, functionName: 'getUsage' })
    }
  }

  useEffect(() => {
    if (isSliderOpen) {
      getUsage()
    }
  }, [isSliderOpen])

  useEffect(() => {
    if (ideas.length !== 0) {
      if (scrollbarsRef && scrollbarsRef.current) {
        scrollbarsRef.current.scrollToBottom()
      }
    }
  }, [ideas])

  const handleClickOpenGenerateIdeasSlider = () => {
    setIsSliderOpen(true)
  }

  const handleClickCloseGenerateIdeasSlider = () => {
    setIsSliderOpen(false)
  }

  const createValidationSchema = (values) => {
    const shapeData = {
      [BUSINESS_ABOUT]: Yup.string().required('Few words about your business are required.'),
      [TARGET_AUDIENCE]: Yup.string().required('Target audience description is required.'),
      [GENERATION_AMOUNT]: Yup.number()
        .min(1, 'Generation amount must be not less than 1.')
        .max(MAX_GENERATION_AMOUNT, `Generation amount must be not more than ${MAX_GENERATION_AMOUNT}.`)
        .positive("Generation amount can't start with a minus.")
        .required('Generation amount is required.'),
    }

    if (values[IMAGE_INCLUDE]) {
      shapeData[IMAGE_KEYWORDS] = Yup.string().required('Image keywords are required.')
    }

    return Yup.object().shape(shapeData)
  }

  const handleClickSubmitForm = async () => {
    formRef.current.handleSubmit()
  }

  const handleSubmitForm = async (values) => {
    try {
      setIsGettingData(true)
      setFormValues({ ...values })

      const await_data = [
        request({
          method: 'POST',
          body: {
            text: `Generate one post for social media for a business that is about ${values[BUSINESS_ABOUT]}, and whose target audience is ${values[TARGET_AUDIENCE]}`,
            type: 'bulk_publications',
            response_amount: values[GENERATION_AMOUNT],
          },
          path: ROUTE_AI,
        }),
      ]

      if (values[IMAGE_INCLUDE]) {
        await_data.push(
          request({
            path: `${ROUTE_MEDIA}/search?q=${values[IMAGE_KEYWORDS]}&system=${TABS_TYPE_IMAGE_UNSPLASH}&type=image`,
          })
        )
      }

      const { 0: response_ai, 1: response_images } = await Promise.all(await_data)

      if (!response_ai || response_ai.error || !response_ai.results || response_ai.results.length === 0) {
        Alert.error(response_ai && response_ai.error ? response_ai.error : ERROR_MESSAGE, { timeout: 5000 })
      } else {
        setIsFormExpanded(false)

        const new_posts = []

        response_ai.results.forEach((text) => {
          const data = {
            id: Number(
              `${Math.floor(Math.random() * 100)}${Math.floor(Math.random() * 100)}${Math.floor(Math.random() * 100)}`
            ),
            text,
            image: null,
          }

          if (response_images && response_images.data) {
            for (let i = 0; i < response_images.data.length; i++) {
              const { url, tempId } = response_images.data[i]
              if (!usedImages[tempId]) {
                usedImages[tempId] = tempId
                data.image = url
                break
              }
            }
          }

          new_posts.push(data)
        })

        setUsedImages({ ...usedImages })
        setIdeas([...ideas, ...new_posts])

        setSelectedPosts([...selectedPosts, ...new_posts])
      }
    } catch (error) {
      errorHelper({ error, componentName: GenerateBulkAIComponent.displayName, functionName: 'handleSubmitForm' })
    } finally {
      setIsGettingData(false)
      getUsage()
    }
  }

  const handleClickUseSelectedPosts = () => {
    if (selectedPosts.length === 0) {
      Alert.error('Please select at least one post to use.', { timeout: 3000 })
      return null
    }

    const data = selectedPosts.map((post) => {
      const { id, text, image } = post

      const data = { tempId: id, postText: text, using_ai: true, postImages: [], postComponent: POST_TEXT }

      if (image) {
        data.postComponent = POST_IMAGE

        data.postImages.push({
          url: image,
          imageURL: image,
          uploadingId: Number(
            `${Math.floor(Math.random() * 100)}${Math.floor(Math.random() * 100)}${Math.floor(Math.random() * 100)}`
          ),
          needsUpload: true,
          type: IMAGE,
        })
      }

      return data
    })

    handleClickAddPosts({ posts: data })

    handleClickCloseGenerateIdeasSlider()
  }

  const handleClickClearGeneratedIdeas = () => {
    setUsedImages({})
    setIdeas([])
    setFormValues({})
    setIsFormExpanded(true)
    setSelectedPosts([])
  }

  const handleClickRemoveGeneratedIdea = ({ id }) => {
    const foundIdeaIndex = ideas.findIndex((idea) => idea.id === id)

    if (foundIdeaIndex > -1) {
      ideas.splice(foundIdeaIndex, 1)
      setIdeas([...ideas])

      if (ideas.length === 0) {
        setIsFormExpanded(true)
      }
    }
  }

  return (
    <Flex>
      {isSliderOpen && (
        <SwipeableDrawer
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
          style={{ zIndex: 2147483001 }}
          anchor="right"
          open={isSliderOpen}
          onClose={handleClickCloseGenerateIdeasSlider}
          disableAutoFocus
          disableEnforceFocus
          onOpen={() => {}}
        >
          <Flex minWidth="560px" maxWidth="560px" height="100%" flexDirection="column" bg="background_slider">
            <Flex flexDirection="column" height="100%">
              <Scrollbars universal ref={scrollbarsRef}>
                <Flex px="m" flexDirection="column" height="100%">
                  <StyledAccordion defaultExpanded expanded={isFormExpanded}>
                    <StyledAccordionSummary
                      onClick={() => {
                        setIsFormExpanded(!isFormExpanded)
                      }}
                      expandIcon={
                        <StyledArrowWrapper>
                          <Icon.VistaSocialChevronDown />
                        </StyledArrowWrapper>
                      }
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
                      <Text fontSize="l" color="primaryText" mb="m">
                        Fill in the fields below and let us do all the magic 🪄
                      </Text>
                    </StyledAccordionSummary>
                    <AccordionDetails>
                      <Formik
                        initialValues={{
                          [BUSINESS_ABOUT]: formValues[BUSINESS_ABOUT] || '',
                          [TARGET_AUDIENCE]: formValues[TARGET_AUDIENCE] || '',
                          [IMAGE_INCLUDE]: formValues[IMAGE_INCLUDE] || false,
                          [IMAGE_KEYWORDS]: formValues[IMAGE_KEYWORDS] || '',
                          [GENERATION_AMOUNT]: formValues[GENERATION_AMOUNT] || DEFAULT_GENERATION_AMOUNT,
                        }}
                        validationSchema={() => {
                          return Yup.lazy((values) => {
                            return createValidationSchema(values)
                          })
                        }}
                        onSubmit={handleSubmitForm}
                        autocomplete="off"
                        enableReinitialize
                        ref={formRef}
                      >
                        {({ values, handleChange, setFieldValue, errors, touched }) => (
                          <Box width="100%">
                            <Box>
                              <Text>What would you like the post to be about?</Text>
                              <TextArea
                                value={values[BUSINESS_ABOUT]}
                                onChange={handleChange(BUSINESS_ABOUT)}
                                placeholder="Healthy breakfasts and exotic food"
                                rows={2}
                                error={errors[BUSINESS_ABOUT] && touched[BUSINESS_ABOUT] && errors[BUSINESS_ABOUT]}
                              />
                            </Box>

                            <Box mt="m">
                              <Text>What is your target audience?</Text>
                              <TextArea
                                value={values[TARGET_AUDIENCE]}
                                onChange={handleChange(TARGET_AUDIENCE)}
                                placeholder="Busy working professionals worldwide"
                                rows={2}
                                error={errors[TARGET_AUDIENCE] && touched[TARGET_AUDIENCE] && errors[TARGET_AUDIENCE]}
                              />
                            </Box>

                            <Box mt="m">
                              <Text>Number of posts to generate</Text>
                              <Input
                                value={values[GENERATION_AMOUNT]}
                                onChange={handleChange(GENERATION_AMOUNT)}
                                label=""
                                error={
                                  errors[GENERATION_AMOUNT] && touched[GENERATION_AMOUNT] && errors[GENERATION_AMOUNT]
                                }
                                type="number"
                                placeholder="Amount to generate"
                              />
                            </Box>

                            <Box mt="m">
                              <Switch
                                isOn={values[IMAGE_INCLUDE]}
                                onClick={() => {
                                  setFieldValue(IMAGE_INCLUDE, !values[IMAGE_INCLUDE])
                                }}
                                text="Include an image"
                                fontSize="s"
                                fontWeight="normal"
                              />
                            </Box>

                            {values[IMAGE_INCLUDE] && (
                              <Box mt="m">
                                <Text>Image keywords</Text>
                                <TextArea
                                  value={values[IMAGE_KEYWORDS]}
                                  onChange={handleChange(IMAGE_KEYWORDS)}
                                  placeholder="Avocado, dragon fruit, eggplants"
                                  rows={2}
                                  error={errors[IMAGE_KEYWORDS] && touched[IMAGE_KEYWORDS] && errors[IMAGE_KEYWORDS]}
                                />
                              </Box>
                            )}
                          </Box>
                        )}
                      </Formik>
                    </AccordionDetails>
                  </StyledAccordion>
                  <Flex flexDirection="column" pb="m">
                    {ideas.map((idea) => {
                      const { id, image, text } = idea

                      const isSelectedIndex = selectedPosts.findIndex((post) => post.id === id)

                      const isSelected = isSelectedIndex > -1

                      return (
                        <StyledIdeaWrapper mt="m" key={id} position="relative">
                          <Flex width="64px" justifyContent="center">
                            <StyledIdeaImageWrapper source={image} fallbackSource="/assets/landscape.svg" />
                          </Flex>

                          <Flex flexDirection="column" ml="m">
                            <StyledGeneratedText color="primaryText">{text}</StyledGeneratedText>
                            <Flex justifyContent="flex-end">
                              <StyledUseIdeaWrapper
                                onClick={() => {
                                  if (isSelected) {
                                    selectedPosts.splice(isSelectedIndex, 1)
                                  } else {
                                    selectedPosts.push(idea)
                                  }
                                  setSelectedPosts([...selectedPosts])
                                }}
                              >
                                {isSelected ? (
                                  <Icon.HeartFull width="18px" height="16px" fill="primary" />
                                ) : (
                                  <Icon.HeartOutlined width="18px" height="16px" fill="primary" />
                                )}

                                <Text ml="s" color="primary">
                                  Use it
                                </Text>
                              </StyledUseIdeaWrapper>
                            </Flex>
                          </Flex>

                          <RemoveIconWrapper
                            onClick={() => {
                              handleClickRemoveGeneratedIdea({ id })
                            }}
                          >
                            <Image width="10px" height="10px" src="/assets/clear.svg" />
                          </RemoveIconWrapper>
                        </StyledIdeaWrapper>
                      )
                    })}
                  </Flex>
                </Flex>
              </Scrollbars>
              <Flex justifyContent="center" p="m">
                {!usage || (usage && remaining > 0) ? (
                  <StyledButtonsWrapper
                    alignItems="center"
                    width="100%"
                    justifyContent={ideas.length === 0 ? 'center' : 'space-between'}
                  >
                    {ideas.length > 0 && (
                      <StyledText
                        onClick={() => {
                          setSelectedPosts([])
                        }}
                        color="secondaryText"
                      >
                        Unselect all
                      </StyledText>
                    )}

                    {ideas.length > 0 && (
                      <StyledText onClick={handleClickClearGeneratedIdeas} color="primary">
                        Clear
                      </StyledText>
                    )}

                    <ButtonWithLoading onClick={handleClickSubmitForm} isSmall type="button" isLoading={isGettingData}>
                      <Text>
                        {isGettingData
                          ? 'Generating'
                          : `${ideas.length > 0 ? 'Generate another post' : 'Generate posts'}  ${
                              usage ? `(${remaining} ${remaining === 1 ? 'credit' : 'credits'} left)` : ''
                            }`}
                      </Text>
                    </ButtonWithLoading>

                    {ideas.length > 0 && (
                      <Fragment>
                        <Button.Gradient isSmall onClick={handleClickUseSelectedPosts}>
                          Use selected
                        </Button.Gradient>

                        <StyledText
                          onClick={() => {
                            setSelectedPosts([...ideas])
                          }}
                          color="secondaryText"
                        >
                          Select all
                        </StyledText>
                      </Fragment>
                    )}
                  </StyledButtonsWrapper>
                ) : (
                  <Button.Primary
                    isSmall
                    onClick={() => {
                      router.push('/pricing')
                    }}
                  >
                    0 credits left. Please upgrade
                  </Button.Primary>
                )}
              </Flex>
            </Flex>
          </Flex>
        </SwipeableDrawer>
      )}
    </Flex>
  )
})

GenerateBulkAIComponent.defaultProps = {}

GenerateBulkAIComponent.propTypes = {
  router: PropTypes.object.isRequired,
}

GenerateBulkAIComponent.displayName = 'GenerateBulkAIComponent'

export default GenerateBulkAIComponent
